import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const PrivacyPolicy = () => {
    return (
        <div style={styles.page}>
            <Helmet>
                <title>Privacy Policy | APILoadTest.com</title>
                <meta name="description" content="Privacy Policy for APILoadTest.com. Learn how we collect, use, and protect your personal information." />
                <link rel="canonical" href="https://www.apiloadtest.com/privacy" />
                <meta name="robots" content="index, follow" />
            </Helmet>

            {/* Header */}
            <header style={styles.header}>
                <div style={styles.headerContainer}>
                    <Link to="/" style={styles.logoSection}>
                        <img src="/logo.png" alt="APILoadTest.com" style={styles.logoImage} />
                        <span style={styles.logoText}>APILoadTest.com</span>
                    </Link>
                </div>
            </header>

            <main style={styles.content}>
                <h1 style={styles.title}>Privacy Policy</h1>
                <p style={styles.effectiveDate}>Effective Date: February 25, 2026</p>

                <section style={styles.section}>
                    <h2 style={styles.heading}>1. Introduction</h2>
                    <p style={styles.paragraph}>
                        APILoadTest.com ("we," "us," or "our") operates the apiloadtest.com website and the APILoadTest platform (the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service.
                    </p>
                    <p style={styles.paragraph}>
                        By using our Service, you agree to the collection and use of information in accordance with this policy.
                    </p>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>2. Information We Collect</h2>
                    <h3 style={styles.subheading}>2.1 Information You Provide</h3>
                    <ul style={styles.list}>
                        <li>Email address (for account creation and login via OTP verification)</li>
                        <li>Payment information (processed securely through our third-party payment processor, Dodo Payments)</li>
                        <li>API endpoint URLs and configurations you submit for load testing</li>
                    </ul>
                    <h3 style={styles.subheading}>2.2 Information Collected Automatically</h3>
                    <ul style={styles.list}>
                        <li>Usage data (pages visited, features used, test configurations)</li>
                        <li>Device and browser information</li>
                        <li>IP address</li>
                        <li>Cookies and similar tracking technologies</li>
                    </ul>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>3. How We Use Your Information</h2>
                    <ul style={styles.list}>
                        <li>To provide, operate, and maintain the Service</li>
                        <li>To process transactions and manage your account</li>
                        <li>To send you transactional emails (OTP codes, purchase confirmations)</li>
                        <li>To monitor and analyze usage and trends to improve the Service</li>
                        <li>To detect, prevent, and address technical issues or abuse</li>
                    </ul>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>4. Data Sharing</h2>
                    <p style={styles.paragraph}>
                        We do not sell, trade, or rent your personal information to third parties. We may share your information only in the following limited circumstances:
                    </p>
                    <ul style={styles.list}>
                        <li><strong>Payment Processing:</strong> Payment data is handled by Dodo Payments. We do not store credit card details on our servers.</li>
                        <li><strong>Analytics:</strong> We use Google Analytics and Vercel Analytics to understand how users interact with our Service. These services collect anonymized usage data.</li>
                        <li><strong>Legal Requirements:</strong> We may disclose your information if required to do so by law or in response to valid requests by public authorities.</li>
                    </ul>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>5. Data Security</h2>
                    <p style={styles.paragraph}>
                        We implement appropriate technical and organizational security measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security.
                    </p>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>6. Data Retention</h2>
                    <p style={styles.paragraph}>
                        We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy. Test results and configurations are retained for the duration of your account's active status.
                    </p>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>7. Your Rights</h2>
                    <p style={styles.paragraph}>
                        You have the right to access, update, or delete your personal information. To exercise these rights, please contact us at <a href="mailto:admin@apiloadtest.com" style={styles.link}>admin@apiloadtest.com</a>.
                    </p>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>8. Cookies</h2>
                    <p style={styles.paragraph}>
                        We use cookies and similar tracking technologies to track activity on our Service and hold certain information. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent.
                    </p>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>9. Third-Party Links</h2>
                    <p style={styles.paragraph}>
                        Our Service may contain links to third-party websites that are not operated by us. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services.
                    </p>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>10. Children's Privacy</h2>
                    <p style={styles.paragraph}>
                        Our Service is not intended for use by children under 18 years of age. We do not knowingly collect personal information from children under 18.
                    </p>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>11. Changes to This Privacy Policy</h2>
                    <p style={styles.paragraph}>
                        We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Effective Date" above.
                    </p>
                </section>

                <section style={styles.section}>
                    <h2 style={styles.heading}>12. Contact Us</h2>
                    <p style={styles.paragraph}>
                        If you have any questions about this Privacy Policy, please contact us at: <a href="mailto:admin@apiloadtest.com" style={styles.link}>admin@apiloadtest.com</a>
                    </p>
                </section>
            </main>

            {/* Footer */}
            <footer style={styles.footer}>
                <div style={styles.footerContent}>
                    <div style={styles.footerLinks}>
                        <Link to="/terms" style={styles.footerLink}>Terms of Service</Link>
                        <Link to="/privacy" style={styles.footerLink}>Privacy Policy</Link>
                        <Link to="/refund" style={styles.footerLink}>Refund Policy</Link>
                        <a href="mailto:admin@apiloadtest.com" style={styles.footerLink}>Contact</a>
                    </div>
                    <p style={styles.copyright}>© 2026 APILoadTest.com. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
};

const styles = {
    page: {
        minHeight: '100vh',
        background: 'var(--light-gray)',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        background: 'var(--primary-white)',
        padding: '16px 0',
        borderBottom: '1px solid var(--border-color)',
    },
    headerContainer: {
        maxWidth: '800px',
        margin: '0 auto',
        padding: '0 24px',
    },
    logoSection: {
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        textDecoration: 'none',
    },
    logoImage: { height: '40px', width: 'auto' },
    logoText: {
        fontFamily: "'Agrandir Narrow', 'Agrandir', Arial, sans-serif",
        fontSize: '24px',
        fontWeight: '700',
        color: 'var(--text-primary)',
    },
    content: {
        maxWidth: '800px',
        margin: '0 auto',
        padding: '48px 24px 80px',
        flex: 1,
    },
    title: {
        fontFamily: "'Agrandir Narrow', 'Agrandir', Arial, sans-serif",
        fontSize: '42px',
        fontWeight: '700',
        color: 'var(--text-primary)',
        marginBottom: '8px',
    },
    effectiveDate: {
        fontSize: '14px',
        color: 'var(--text-secondary)',
        marginBottom: '48px',
    },
    section: { marginBottom: '36px' },
    heading: {
        fontSize: '22px',
        fontWeight: '700',
        color: 'var(--text-primary)',
        marginBottom: '12px',
    },
    subheading: {
        fontSize: '18px',
        fontWeight: '600',
        color: 'var(--text-primary)',
        marginBottom: '8px',
        marginTop: '16px',
    },
    paragraph: {
        fontSize: '16px',
        lineHeight: '1.8',
        color: 'var(--text-primary)',
        marginBottom: '12px',
    },
    list: {
        paddingLeft: '24px',
        fontSize: '16px',
        lineHeight: '1.8',
        color: 'var(--text-primary)',
        marginBottom: '12px',
    },
    link: { color: '#6366f1', textDecoration: 'none', fontWeight: '600' },
    footer: {
        background: 'var(--primary-white)',
        padding: '32px 24px',
        borderTop: '1px solid var(--border-color)',
    },
    footerContent: { maxWidth: '800px', margin: '0 auto', textAlign: 'center' },
    footerLinks: {
        display: 'flex',
        gap: '24px',
        justifyContent: 'center',
        marginBottom: '16px',
        flexWrap: 'wrap',
    },
    footerLink: {
        color: 'var(--text-secondary)',
        textDecoration: 'none',
        fontSize: '14px',
    },
    copyright: { fontSize: '14px', color: 'var(--text-secondary)' },
};

export default PrivacyPolicy;
